
// ==================== Detail Panel ====================
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import User from "../../models/User";
import { useAuth } from "../../providers/AuthProvider";


// ==================== Badge ====================
function CategoryBadge({ cat }) {
    return (
        <span className={`text-[10.5px] leading-[15px] font-semibold px-[7px] py-[2px] rounded-full whitespace-nowrap ${cat.tagStyles || BADGE_STYLES.broadcast}`}>
            {cat.tag}
        </span>
    );
    // return (
    //     <span className={`text-[10.5px] font-semibold px-[7px] py-[2px] rounded-full whitespace-nowrap ${BADGE_STYLES[cat] || BADGE_STYLES.broadcast}`}>
    //         {BADGE_LABELS[cat] || cat}
    //     </span>
    // );
}

function ProjectBadge({ name }) {
    return (
        <span className="text-[10.5px] font-medium px-[7px] py-[2px] rounded bg-indigo-50 text-indigo-600 border border-indigo-200">
            {name}
        </span>
    );
}

const BADGE_STYLES = {
    broadcast: 'bg-slate-100 text-slate-600',
    discipline: 'bg-violet-100 text-violet-800',
    geo: 'bg-teal-100 text-teal-800',
    interest: 'bg-amber-100 text-amber-800',
    shortlisted: 'bg-orange-100 text-orange-900',
    team: 'bg-emerald-100 text-emerald-800',
    specific: 'bg-violet-100 text-violet-800',
};

const BADGE_LABELS = {
    broadcast: 'Announcement',
    discipline: 'Discipline',
    geo: 'Location',
    interest: 'Project Interest',
    shortlisted: 'Shortlisted',
    team: 'Assignment Team',
    specific: 'Specific Talent',
};

const FILTER_TABS = [
    { key: 'all', label: 'All' },
    { key: 'unread', label: 'Unread' },
    { key: 'project', label: 'Projects' },
    { key: 'announcement', label: 'Announcements' },
    // { key: 'archived', label: 'Archived' },
];

const EMPTY_STATES = {
    all: { title: 'No messages yet', sub: 'Messages will appear here' },
    unread: { title: "You're all caught up", sub: 'All messages have been read' },
    project: { title: 'No project messages', sub: 'Project updates will appear here' },
    announcement: { title: 'No announcements', sub: 'Platform announcements will appear here' },
    archived: { title: 'Nothing archived', sub: 'Archived messages will appear here' },
};

const CATEGORY_MAP = {
    // 2: 'position',
    // 3: 'opp',
    // null: 'announcement',
    1: {
        type: 'announcement',
        tag: 'Announcement',
        tagStyles: 'bg-slate-100 text-slate-600',
    },
    2: {
        type: 'position',
        tag: 'Matching',
        tagStyles: 'bg-violet-100 text-violet-800',
    },
    3: {
        type: 'opp',
        tag: 'Shortlisted',
        tagStyles: 'bg-orange-100 text-orange-900',
    },
}

function ActionBadge({ actionType }) {
    if (actionType === 'info') return null;
    return (
        <span className="text-[10.5px] font-semibold px-[7px] py-[2px] rounded-full whitespace-nowrap bg-amber-100 text-amber-800">
            {actionType.charAt(0).toUpperCase() + actionType.slice(1)} Needed
        </span>
    );
}

const SUBJECT_MAP = {
    "App\\Project": '/talent/project',
    // "App\\ProjectPosition": '/talent/project',
    "App\\ProjectPosition": '/talent/job-board/job-details',
}

const ACTIONS_MAP = {
    1: 'View Project',
    2: 'Confirm Availability',
    3: 'Edit Profile',
    4: 'View Opportunities',
    5: 'View Job Board',
}

const CATEGORY_MAP2 = {
    1: 'announcement',
    2: 'position',
    3: 'opp',
}

const RenderAction = ({ detail }) => {
    if (!detail.action) return <div />
    if (detail.action === 1) {
        return (
            <div className="bg-white border border-slate-200 rounded-[10px] p-4 mb-6">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 mb-2.5">Related Job</div>
                <div className="text-[15px] font-semibold mb-1">{detail.project?.name}</div>
                <div className="text-[12.5px] text-slate-500">{detail.project_position?.title} · {detail.client?.name}</div>
                <div className="pt-4">
                    {/* <Link to={`${SUBJECT_MAP[detail.subject_type]}/${detail.subject_id}`} className="btn btn-primary"> */}
                    <Link to={detail.action_link} disabled={!detail.action_link} className="btn btn-primary">
                        {detail.action_title}
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <Link to={detail.action_link} disabled={!detail.action_link} className="btn btn-primary self-start">
            {detail.action_title}
        </Link>
    )
}

const RenderWhy = ({ detail }) => {
    const { auth } = useAuth();
    const filters = detail.system_notification?.filters || {};

    let message;
    if (detail.category === 2) {
        message = <>You match the requirements for the <strong className="text-primary-light">{detail.project?.name}</strong> role.</>;
    } else if (detail.category === 3) {
        message = <>You were shortlisted for the <strong className="text-primary-light">{detail.project?.name}</strong> role.</>;
    } else if (detail.category === 1) {
        const matchedDisciplines = (filters.discipline_ids?.length > 0)
            ? (auth.talent_disciplines || [])
                .filter(d => filters.discipline_ids.includes(d.id))
                .map(d => TB.pluralize(d.name))
            : [];

        const formatList = (items) => {
            if (items.length <= 2) return items.join(' and ');
            return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1];
        };

        let locationSuffix = '';
        if (filters.visibility_setting_location === 2) {
            locationSuffix = ' in the region';
        } else if (filters.visibility_setting_location >= 4) {
            locationSuffix = ` ${laravel.project_visibility_location_settings[filters.visibility_setting_location]?.toLowerCase()} of ${filters.address || `${auth.location?.city}, ${auth.location?.state}`}`;
        }

        if (filters.only_user_ids?.includes(auth.id)) {
            message = 'Sent directly to you';
        } else if (filters.added_user_ids?.includes(auth.id)) {
            message = 'Sent to all talent';
        } else if (matchedDisciplines.length > 0) {
            const disciplineStr = formatList(matchedDisciplines);
            message = `Sent to ${disciplineStr}${locationSuffix}`;
        } else if (locationSuffix) {
            message = `Sent to talent${locationSuffix}`;
        } else {
            message = 'Sent to all talent';
        }
    } else {
        message = 'Sent to all talent.';
    }

    return (
        <>
            <div className="h-px bg-slate-200 my-6 xl:hidden" />
            <div className="bg-indigo-50/60 border border-indigo-100 rounded-[10px] p-3.5 mb-6 flex gap-2.5 self-start">
                <div className="text-indigo-500 shrink-0 mt-px">
                    <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                </div>
                <div>
                    <div className="text-[10.5px] font-bold uppercase tracking-wide text-indigo-500 mb-[3px]">Why you received this</div>
                    <p className="text-[13px] text-indigo-900/70 leading-normal">{message}</p>
                </div>
            </div>
        </>
    )
}

const TalentMessageDetailView = ({ msg, loading, isRead, onMarkUnread, onMarkRead, onArchive, onDelete }) => {
    if (loading) return <SkeletonDetailView />
    const bodyHtml = msg.body?.replace(/\n/g, '<br>').replace(/•/g, '&bull;');
    const cat = CATEGORY_MAP[msg.category];

    return (
        <div className="flex-1 flex flex-col overflow-y-auto bg-slate-50">
            {/* Action bar */}
            {(onMarkRead || onMarkUnread || onDelete) && (
                <div className="flex items-center gap-2 px-8 py-3.5 border-b border-slate-200 bg-white sticky top-0 z-10 shrink-0">
                    {onMarkUnread && onMarkRead && (
                        <button
                            className="flex items-center gap-[5px] px-3 py-1.5 border border-primary-lightest rounded-md bg-transparent text-[12.5px] font-medium text-primary cursor-pointer transition-colors hover:bg-primary/10 hover:text-primary-darker hover:border-primary-lightest"
                            onClick={() => isRead ? onMarkUnread(msg.id) : onMarkRead(msg.id)}
                            disabled={loading}
                        >
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
                            {isRead ? 'Mark unread' : 'Mark read'}
                        </button>
                    )}
                    {onDelete && (
                        <button
                            className="flex ml-auto items-center gap-[5px] px-3 py-1.5 border border-red-200 bg-red-50 rounded-md bg-transparent text-[12.5px] font-medium text-red-500 cursor-pointer transition-colors hover:bg-red-100 hover:text-red-900"
                            onClick={async () => {
                                const shouldDelete = await TB.alert(
                                    'error',
                                    'Are you sure?',
                                    'Deleting this message cannot be undone',
                                    [{ title: 'Yes, delete' }, { title: 'Cancel' },]
                                );

                                if (shouldDelete) {
                                    onDelete(msg.id);
                                }
                            }}
                            disabled={loading}
                        >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                        </button>
                    )}
                </div>
            )}
            {/* Content */}
            <div className="px-8 py-7 max-w[640px]">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    {msg.is_urgent && (
                        <span className="text-[10.5px] leading-[15px] font-semibold px-[7px] gap-1.5 flex items-center py-[2px] rounded-full whitespace-nowrap bg-red-100 text-red-700">
                            <i className="fas fa-flag text-red900" />
                            Urgent
                        </span>
                    )}
                    <CategoryBadge cat={cat} />
                </div>

                <h1 className="text-xl font-bold tracking-tight leading-snug mb-2.5">{msg.title}</h1>

                <div className="flex items-center gap-3.5 mb-6 flex-wrap">
                    <span className="text-[12.5px] text-slate-500 flex items-center gap-[5px]">
                        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                        {msg.sender_name || 'N/A'}
                    </span>
                    <Tooltip placement="bottom" title={TB.moment(msg.created_at).format('lll')}>
                        <span className="text-[12.5px] text-slate-500 flex items-center gap-[5px]">
                            <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12,6 12,12 16,14" /></svg>
                            {TB.moment(msg.created_at).fromNow()}
                        </span>
                    </Tooltip>
                </div>

                <div
                    className="text-[14.5px] leading-[1.75] text-slate-700 mb-7"
                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                />

                {/* CTAs */}
                {msg.ctas?.length > 0 && (
                    <>
                        <div className="flex gap-2.5 flex-wrap">
                            {msg.ctas.map((cta, i) => (
                                <button
                                    key={i}
                                    className={cta.primary
                                        ? 'px-5 py-2.5 bg-indigo-600 text-white border-none rounded-md text-[13.5px] font-semibold cursor-pointer transition-colors hover:bg-indigo-700'
                                        : 'px-5 py-2.5 bg-transparent text-slate-500 border border-slate-200 rounded-md text-[13.5px] font-medium cursor-pointer transition-colors hover:bg-slate-50 hover:text-slate-900'
                                    }
                                >
                                    {cta.label}
                                </button>
                            ))}
                        </div>
                        <div className="h-px bg-slate-200 my-6" />
                    </>
                )}

                <div className="xl:flex gap-6 border-t pt-6 mt-6">
                    <div className="flex-1">
                        <RenderAction detail={msg} />
                    </div>
                    <div className="flex-1">
                        <RenderWhy detail={msg} />
                    </div>
                </div>

                {msg.ctas?.length === 0 && (
                    <div className="text-[12.5px] text-slate-400">No action required — this message is for information only.</div>
                )}
            </div>
        </div>
    );
}

const SkeletonDetailView = () => {
    return (
        <div className="flex-1 flex flex-col overflow-y-auto bg-slate-50">
            <div className="flex justify-between items-center gap-2 px-8 py-3.5 border-b border-slate-200 bg-white sticky top-0 z-10 shrink-0">
                <div className="skeleton h-[34px] w-[126px]" />
                <div className="skeleton h-[34px] w-[90px]" />
            </div>

            <div className="px-8 py-7 max-w-[640px]">
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <span className="h-4 rounded-full skeleton w-16" />
                </div>

                <div className="skeleton h-7 w-5/6 mb-2.5" />

                <div className="flex items-center gap-3.5 mb-6 pt-1 flex-wrap">
                    <span className="text-[12.5px] text-slate-500 flex items-center gap-[5px]">
                        <div className="skeleton h-4 w-[120px]" />
                    </span>
                    <span className="text-[12.5px] text-slate-500 flex items-center gap-[5px]">
                        <div className="skeleton h-4 w-[100px]" />
                    </span>
                </div>

                <div className="flex1 flex flex-col gap-2.5">
                    <div className="skeleton h-5 w-full" />
                    <div className="skeleton h-5 w-5/6" />
                    <div className="skeleton h-5 w-7/12" />
                    <div className="skeleton h-5 w-5/6" />
                    <div className="skeleton h-5 w-9/12" />
                    <div className="skeleton h-5 w-4/5" />
                    <div className="skeleton h-5 w-8/12" />
                    <div className="skeleton h-5 w-5/6" />
                    <div className="skeleton h-5 w-9/12" />
                    <div className="skeleton h-5 w-4/5" />
                </div>
            </div>
        </div>
    );
}

export {
    SkeletonDetailView,
    TalentMessageDetailView,
    RenderWhy,
    RenderAction
}